import { formatPrice, normaliseShopifyPrice } from '@utils/prices';

import { CurrencyCode } from '../../../types/Locations';

export type CheckoutOrderTotals = {
  freeShipping?: boolean;
  isEstimate: boolean;
  maxOrderValue: string;
  orderValue: string;
  shippingCost?: string;
  taxRate?: number;
  taxValue?: string;
};

export type LinkedDrawEntryQuote = {
  currencyCode: string;
  maxCharge: number;
  maxShippingCharge: number;
  maxTaxCharge: number;
  minCharge: number;
  minShippingCharge: number;
  minTaxCharge: number;
};

export type DrawEntryPricing = {
  currencyCode: string;
  includesCalculatedShipping: boolean;
  subtotalItemsPrice: number;
  subtotalShippingPrice: number;
  subtotalTaxPrice: number;
  taxRate: number;
  totalPrice: number;
};

const resolveTaxRate = (quote: LinkedDrawEntryQuote): number | undefined => {
  const taxableAmount = quote.maxCharge - quote.maxTaxCharge;

  if (quote.maxTaxCharge === 0 || taxableAmount <= 0) return undefined;

  return Math.round((quote.maxTaxCharge / taxableAmount) * 1000) / 10;
};

const formatAmount = (amount: number, currencyCode: CurrencyCode): string => {
  return String(
    formatPrice(normaliseShopifyPrice(amount, currencyCode), currencyCode),
  );
};

const formatRange = (
  min: number,
  max: number,
  currencyCode: CurrencyCode,
): string => {
  if (min === max) return formatAmount(max, currencyCode);

  return `${formatAmount(min, currencyCode)}–${formatAmount(max, currencyCode)}`;
};

export const buildQuoteOrderTotals = (
  quote: LinkedDrawEntryQuote,
): CheckoutOrderTotals => {
  const currencyCode = quote.currencyCode as CurrencyCode;

  const isEstimate = quote.minCharge !== quote.maxCharge;

  const maxOrderValue = formatAmount(quote.maxCharge, currencyCode);

  const orderValue = formatRange(
    quote.minCharge - quote.minShippingCharge - quote.minTaxCharge,
    quote.maxCharge - quote.maxShippingCharge - quote.maxTaxCharge,
    currencyCode,
  );

  const shippingCost =
    quote.maxShippingCharge > 0
      ? formatRange(
          quote.minShippingCharge,
          quote.maxShippingCharge,
          currencyCode,
        )
      : undefined;

  const taxRate = resolveTaxRate(quote);

  const taxValue =
    quote.maxTaxCharge > 0
      ? formatRange(quote.minTaxCharge, quote.maxTaxCharge, currencyCode)
      : undefined;

  return {
    freeShipping: quote.maxShippingCharge === 0,
    isEstimate,
    maxOrderValue,
    orderValue,
    shippingCost,
    taxRate,
    taxValue,
  };
};

export const buildPricingOrderTotals = (
  pricing: DrawEntryPricing,
): CheckoutOrderTotals => {
  const currencyCode = pricing.currencyCode as CurrencyCode;
  const hasShipping = pricing.subtotalShippingPrice > 0;
  const hasTax = pricing.subtotalTaxPrice > 0;

  return {
    freeShipping: pricing.includesCalculatedShipping && !hasShipping,
    isEstimate: false,
    maxOrderValue: formatAmount(pricing.totalPrice, currencyCode),
    orderValue: formatAmount(pricing.subtotalItemsPrice, currencyCode),
    shippingCost: hasShipping
      ? formatAmount(pricing.subtotalShippingPrice, currencyCode)
      : undefined,
    taxRate: hasTax ? pricing.taxRate : undefined,
    taxValue: hasTax
      ? formatAmount(pricing.subtotalTaxPrice, currencyCode)
      : undefined,
  };
};
